export const PREVIEW_MODE_KEY = 'kea_storefront_preview_mode_v1';
export const PREVIEW_FEEDBACK_KEY = 'kea_storefront_preview_feedback_v1';
export const PREVIEW_DRAFT_KEY = 'kea_storefront_preview_draft_v1';

export const PREVIEW_MODE_ENABLED = 'enabled';
export const PREVIEW_MODE_DISABLED = 'disabled';

export const PREVIEW_FEEDBACK_VERSION = 1;

const RESULTS_TARGET_STATUSES = new Set([
  'ok',
  'not_required',
  'missing_container',
  'route_404',
  'runtime_error',
  'unknown',
]);

const SELECTOR_SOURCES = new Set([
  'auto_marker',
  'auto_predefined_selector',
  'manual_override',
  'unknown',
]);

const FORBIDDEN_STRING_PATTERNS = [
  /https?:\/\/[^\s]*admin[^\s]*/i,
  /\btoken\b/i,
  /\bsecret\b/i,
  /\bcredential/i,
  /\bbearer\b/i,
];

function resolveStorage(storage) {
  if (storage !== undefined) {
    return storage;
  }

  if (typeof window === 'undefined') {
    return null;
  }

  return window.localStorage;
}

function safeSetItem(storage, key, value) {
  try {
    storage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

function sanitizeString(value) {
  if (typeof value !== 'string') {
    return '';
  }

  if (FORBIDDEN_STRING_PATTERNS.some((pattern) => pattern.test(value))) {
    return '[redacted]';
  }

  return value;
}

function normalizeTimestamp(value, nowIso) {
  if (!value) {
    return nowIso;
  }

  const timestamp = new Date(value).toISOString();
  return timestamp;
}

function normalizeCount(value) {
  if (!Number.isFinite(value)) {
    return 0;
  }

  return Math.max(0, Math.floor(value));
}

function normalizeMode(mode) {
  return ['widget', 'classic', 'combined'].includes(mode) ? mode : 'widget';
}

function normalizePage(page = {}) {
  return {
    runtime_expected: !!page.runtime_expected,
    mounted_runtime_count: normalizeCount(page.mounted_runtime_count),
    pre_mount_failures: normalizeCount(page.pre_mount_failures),
  };
}

function normalizeSearchInput(searchInput = {}) {
  const selectorSource = SELECTOR_SOURCES.has(searchInput.selector_source)
    ? searchInput.selector_source
    : 'unknown';

  return {
    found: !!searchInput.found,
    selector_source: selectorSource,
  };
}

function deriveResultsRequired(mode, explicitRequired) {
  if (typeof explicitRequired === 'boolean') {
    return explicitRequired;
  }

  return mode === 'classic' || mode === 'combined';
}

function normalizeResultsTarget(resultsTarget = {}, mode) {
  const required = deriveResultsRequired(mode, resultsTarget.required);
  const fallbackStatus = required ? 'unknown' : 'not_required';
  const status = RESULTS_TARGET_STATUSES.has(resultsTarget.status)
    ? resultsTarget.status
    : fallbackStatus;

  return {
    required,
    found: !!resultsTarget.found,
    status,
  };
}

function normalizeMessageEntry(entry, nowIso) {
  if (typeof entry === 'string') {
    return {
      code: 'unknown',
      message: sanitizeString(entry),
      timestamp: nowIso,
    };
  }

  return {
    code: sanitizeString(entry?.code || 'unknown'),
    message: sanitizeString(entry?.message || ''),
    timestamp: normalizeTimestamp(entry?.timestamp, nowIso),
  };
}

export function normalizePreviewFeedback(feedback = {}, now = new Date()) {
  const nowIso = now instanceof Date ? now.toISOString() : new Date(now).toISOString();
  const mode = normalizeMode(feedback.mode);

  return {
    version: PREVIEW_FEEDBACK_VERSION,
    updated_at: nowIso,
    preview_enabled: !!feedback.preview_enabled,
    mode,
    page: normalizePage(feedback.page),
    search_input: normalizeSearchInput(feedback.search_input),
    results_target: normalizeResultsTarget(feedback.results_target, mode),
    warnings: Array.isArray(feedback.warnings)
      ? feedback.warnings.map((entry) => normalizeMessageEntry(entry, nowIso))
      : [],
    errors: Array.isArray(feedback.errors)
      ? feedback.errors.map((entry) => normalizeMessageEntry(entry, nowIso))
      : [],
    has_draft_settings: !!feedback.has_draft_settings,
  };
}

export function setPreviewMode(value, storage) {
  if (value !== PREVIEW_MODE_ENABLED && value !== PREVIEW_MODE_DISABLED) {
    throw new Error('Preview mode must be enabled or disabled');
  }

  const targetStorage = resolveStorage(storage);
  if (!targetStorage) {
    return false;
  }

  return safeSetItem(targetStorage, PREVIEW_MODE_KEY, value);
}

export function writePreviewFeedback(feedback, storage, now = new Date()) {
  const targetStorage = resolveStorage(storage);
  const normalized = normalizePreviewFeedback(feedback, now);

  if (!targetStorage) {
    return normalized;
  }

  safeSetItem(targetStorage, PREVIEW_FEEDBACK_KEY, JSON.stringify(normalized));

  return normalized;
}
